import {type TypedRoute, useRoute} from '@typed-router';
import {storeToRefs} from 'pinia';
import {useDataTracking, useLogger} from '#imports';
import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
import {useFreelancerSignupNavigationStore} from '~/features/freelancer-signup/navigation/store/FreelancerSignupNavigationStore';
import {FreelancerSignupStepName} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
import {
    FreelancerSignupTrackingActionContextStepSaved,
    FreelancerSignupTrackingContext,
    FreelancerSignupTrackingEntryPoint,
    FreelancerSignupTrackingEvent,
    FreelancerSignupTrackingEventActionPerformedActionName,
    type FreelancerSignupTrackingEventActionPerformedPayload,
    type FreelancerSignupTrackingEventCreationStartedPayload,
    type FreelancerSignupTrackingEventStepViewPayload,
    FreelancerSignupTrackingStepName,
    FreelancerSignupTrackingVertical,
} from '~/features/freelancer-signup/track/types/FreelancerSignupTracking';
import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
import {useFeatureContextStore} from '~/store/featureStore';

export function useFreelancerSignupTracking() {
    const FUNNEL_VERSION = '2';

    // Composables
    const {track} = useDataTracking();
    const logger = useLogger();
    const route = useRoute();

    // Stores
    const {currentStep} = storeToRefs(useFreelancerSignupNavigationStore());
    const {workflow} = storeToRefs(useFreelancerSignupStore());
    const {features} = useFeatureContextStore();

    function creationStarted(): void {
        const context = workflow.value?.context ?? _toContextWhenUnloggued(route);
        const payload: FreelancerSignupTrackingEventCreationStartedPayload = {
            workflow_id: workflow.value?.id || '',
            vertical: _toVertical(context),
            context: _toContext(context),
            entry_point: _toEntryPoint(route, context),
            version: FUNNEL_VERSION,
        };

        _trackAndLog(FreelancerSignupTrackingEvent.CREATION_STARTED, payload);
    }

    /**
     * @param currentStepName: provides the step name to track or use the one define via the current step
     */
    function stepSaved(trackinsInfos?: {
        currentStepName?: FreelancerSignupTrackingStepName;
        actionContext?: FreelancerSignupTrackingActionContextStepSaved;
    }): void {
        const stepName = trackinsInfos?.currentStepName || _toStepName(currentStep.value.name);
        if (!stepName) {
            return;
        }

        const context = workflow.value?.context ?? _toContextWhenUnloggued(route);
        const payload: FreelancerSignupTrackingEventActionPerformedPayload = {
            action_name: FreelancerSignupTrackingEventActionPerformedActionName.STEP_SAVED,
            step_name: stepName,
            workflow_id: workflow.value?.id || '',
            vertical: _toVertical(context),
            context: _toContext(context),
            action_context: trackinsInfos?.actionContext || FreelancerSignupTrackingActionContextStepSaved.NONE,
        };

        _trackAndLog(FreelancerSignupTrackingEvent.ACTION_PERFORMED, payload);
    }

    /**
     * @param currentStepName: provides the step name to track or use the one define via the current step
     */
    function stepSkipped(trackinsInfos?: {
        currentStepName?: FreelancerSignupTrackingStepName;
        actionContext?: FreelancerSignupTrackingActionContextStepSaved;
    }): void {
        const stepName = trackinsInfos?.currentStepName || _toStepName(currentStep.value.name);
        if (!stepName) {
            return;
        }

        const context = workflow.value?.context ?? _toContextWhenUnloggued(route);
        const payload: FreelancerSignupTrackingEventActionPerformedPayload = {
            action_name: FreelancerSignupTrackingEventActionPerformedActionName.STEP_SKIPPED,
            step_name: stepName,
            workflow_id: workflow.value?.id || '',
            vertical: _toVertical(context),
            context: _toContext(context),
            action_context: trackinsInfos?.actionContext || FreelancerSignupTrackingActionContextStepSaved.NONE,
        };

        _trackAndLog(FreelancerSignupTrackingEvent.ACTION_PERFORMED, payload);
    }

    /**
     * @param currentStepName: provides the step name to track or use the one define via the current step
     */
    function stepViewed(currentStepName?: FreelancerSignupTrackingStepName): void {
        const stepName = currentStepName || _toStepName(currentStep.value.name);
        if (!stepName) {
            return;
        }

        const context = workflow.value?.context ?? _toContextWhenUnloggued(route);
        const payload: FreelancerSignupTrackingEventStepViewPayload = {
            step_name: stepName,
            workflow_id: workflow.value?.id || '',
            vertical: _toVertical(context),
            context: _toContext(context),
        };
        _trackAndLog(FreelancerSignupTrackingEvent.STEP_VIEWED, payload);
    }

    function _trackAndLog(eventName: string, eventProperties?: any, userProperties?: any) {
        track(eventName, eventProperties, userProperties);
        if (import.meta.dev) {
            logger.info(`👁️ FreelancerSignupTracking: ${eventName}`, eventProperties);
        }
    }

    function _toStepName(stepName: FreelancerSignupStepName): FreelancerSignupTrackingStepName | undefined {
        switch (stepName) {
            case FreelancerSignupStepName.WELCOME_BACK:
                return FreelancerSignupTrackingStepName.WELCOME_BACK;
            case FreelancerSignupStepName.REMINDER:
                return FreelancerSignupTrackingStepName.REMINDER;
            // Funnel steps
            case FreelancerSignupStepName.WELCOME_TO_MALT_REGULAR:
            case FreelancerSignupStepName.WELCOME_TO_MALT_OPEN:
            case FreelancerSignupStepName.WELCOME_TO_MALT_STRATEGY:
                return FreelancerSignupTrackingStepName.WELCOME_TO_MALT;
            case FreelancerSignupStepName.FREELANCING_EXPERIENCE:
                return FreelancerSignupTrackingStepName.FREELANCING_EXPERIENCE;
            case FreelancerSignupStepName.DESCRIBE_YOURSELF:
                return FreelancerSignupTrackingStepName.DESCRIBE_YOURSELF;
            case FreelancerSignupStepName.CREATE_YOUR_ACCOUNT:
                return FreelancerSignupTrackingStepName.EMAIL_ACCOUNT_CREATION;
            case FreelancerSignupStepName.COMPLETE_YOUR_ACCOUNT:
                return FreelancerSignupTrackingStepName.ACCOUNT_SSO_COMPLETION;
            case FreelancerSignupStepName.VERIFY_YOUR_EMAIL:
                return FreelancerSignupTrackingStepName.EMAIL_VERIFICATION;
            case FreelancerSignupStepName.SHOWCASE_YOUR_TALENTS:
                return FreelancerSignupTrackingStepName.COMPLETION_MODE_SELECTION;
            case FreelancerSignupStepName.WORK_EXPERIENCE:
                return FreelancerSignupTrackingStepName.EXPERIENCES;
            case FreelancerSignupStepName.EDUCATION:
                return FreelancerSignupTrackingStepName.EDUCATION;
            case FreelancerSignupStepName.JOB_TITLE_AND_CATEGORY:
                // TranckingStepName.CATEGORY_SELECTION is tracked in the step
                return FreelancerSignupTrackingStepName.JOB_TITLE;
            case FreelancerSignupStepName.YOUR_FUNCTIONAL_AND_GLOBAL_SKILLS:
                return FreelancerSignupTrackingStepName.YOUR_FUNCTIONAL_AND_GLOBAL_SKILLS;
            case FreelancerSignupStepName.YOUR_TOP_SKILLS:
                return FreelancerSignupTrackingStepName.TOP_SKILLS;
            case FreelancerSignupStepName.WORKING_LANGUAGES:
                return FreelancerSignupTrackingStepName.WORKING_LANGUAGES;
            case FreelancerSignupStepName.LEVEL_OF_EXPERIENCE:
                return FreelancerSignupTrackingStepName.EXPERIENCE_LEVEL;
            case FreelancerSignupStepName.DAILY_RATE:
                return FreelancerSignupTrackingStepName.DAILY_RATE;
            case FreelancerSignupStepName.LOCATION_AND_WORKPLACE_PREFERENCE:
                return FreelancerSignupTrackingStepName.WORKING_LOCATION;
            case FreelancerSignupStepName.YOUR_PROFILE_PICTURE:
                return FreelancerSignupTrackingStepName.PROFILE_PICTURE;
            case FreelancerSignupStepName.YOUR_PHONE_NUMBER:
                return FreelancerSignupTrackingStepName.PHONE_NUMBER;
            case FreelancerSignupStepName.YOUR_PRIVACY_SETTINGS:
                return FreelancerSignupTrackingStepName.PRIVACY_SETTINGS;
            default:
                if (import.meta.dev) {
                    logger.error(`👁️ FreelancerSignupTracking: no step name found ${stepName}`);
                }

                return undefined;
        }
    }

    function _toContext(context: FreelancerSignupContext | undefined): FreelancerSignupTrackingContext {
        switch (context) {
            case FreelancerSignupContext.OPEN:
                return FreelancerSignupTrackingContext.OPEN;
            default:
                return FreelancerSignupTrackingContext.NONE;
        }
    }

    function _toEntryPoint(route: TypedRoute, context: FreelancerSignupContext | undefined): FreelancerSignupTrackingEntryPoint {
        if (route.name === 'freelancer-signup-index-redirect-to-regular') {
            return FreelancerSignupTrackingEntryPoint.MALT_STRATEGY_REDIRECTION;
        }

        if (route.query.mfi) {
            return FreelancerSignupTrackingEntryPoint.FREELANCER_INVITATION;
        }

        if (context === FreelancerSignupContext.OPEN) {
            return FreelancerSignupTrackingEntryPoint.MALT_OPEN_INVITATION;
        }

        return FreelancerSignupTrackingEntryPoint.NONE;
    }

    function _toVertical(context: FreelancerSignupContext | undefined): FreelancerSignupTrackingVertical {
        switch (context) {
            case FreelancerSignupContext.STRATEGY:
                return FreelancerSignupTrackingVertical.STRATEGY;
            case FreelancerSignupContext.OPEN:
            case FreelancerSignupContext.REGULAR:
            default:
                return FreelancerSignupTrackingVertical.REGULAR;
        }
    }

    function _toContextWhenUnloggued(route: TypedRoute): FreelancerSignupContext {
        if (route.name === 'freelancer-signup-index-welcome-to-malt-strategy') {
            return FreelancerSignupContext.STRATEGY;
        }
        if (route.name === 'freelancer-signup-index-welcome-to-malt-open') {
            return FreelancerSignupContext.OPEN;
        }
        return FreelancerSignupContext.REGULAR;
    }

    return {
        creationStarted,
        stepSaved,
        stepSkipped,
        stepViewed,
    };
}
